import { formatNumber, useAppContext } from "application";
import { catalogApi } from "implementations";
import { useCallback, useEffect, useState } from "react";
import { UsefulLinks } from "typing";
import { theme } from "ui";
import FlixMedia, { flixMediaObject } from "./components/FlixMedia";
import StandOut from "./components/StandOut";
import Syndigo from "./components/Syndigo";
import {
  AggregatorCustom,
  BodyTable,
  BoxAlign,
  BoxDimension,
  BoxLinks,
  BoxSpecs,
  ButtonCustom,
  CellTable,
  CollapseArrowDown,
  CollapseArrowUp,
  Container,
  ContainerInfo,
  DimensionArrowHeight,
  DimensionArrowLength,
  DimensionArrowWidth,
  DimensionWeightIcon,
  Group,
  LineTable,
  LinkPDF,
  Margin1,
  PDFArrow,
  Row,
  SectionInfo,
  SectionSub,
  SubtitleDimension,
  Table,
  TextCustom,
  Title,
  TitleBlockTable,
  Value,
} from "./styles";

interface params {
  idProduct?: string;
  brand?: string;
  idSection: string;
  hasProductSpecCalled: (value: boolean) => void;
  handleSize?: (size: string) => void;
  handleModel?: (model: string) => void;
  isBlackTheme?: boolean;
  bareanId13?: string;
}

const ProductSpec = ({
  idProduct,
  brand,
  idSection,
  hasProductSpecCalled,
  handleSize,
  handleModel,
  isBlackTheme,
  bareanId13,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
params) => {
  const { data: dimensionData } = catalogApi.getProductDimension(
    idProduct || "",
    Boolean(idProduct)
  );
  const { data: linksData } = catalogApi.getProductUseLinks(
    idProduct || "",
    Boolean(idProduct)
  );
  const { data: specsData = [], isLoading } = catalogApi.getProductSpecs(
    idProduct || "",
    Boolean(idProduct)
  );
  const [modelProduct, setModelProduct] = useState<string | undefined>();
  const [isModelValueLoaded, setIsModelValueLoaded] = useState<boolean>(false);

  const [sizeProduct, setSizeProduct] = useState<string | undefined>();
  const [isSizeValueLoaded, setIsSizeValueLoaded] = useState<boolean>(false);

  const buildUrlUsefulLinks = useCallback((document: UsefulLinks) => {
    return decodeURIComponent(
      document?.s3Url ||
        `https://fcx-s3-fercos-backoffice.ferreiracosta.com/documento_produto/${document.codeCD}/${document.sequenceNumber}_${document.imageTypeCode}_${document.codeCD}.pdf`
    );
  }, []);

  useEffect(() => {
    if (specsData && specsData?.length > 0) {
      const REF_MODEL_CODE = specsData?.find((item) => item.id === 0);
      const REF_MODEL = REF_MODEL_CODE?.specs.find(
        (item) => item.name === "Modelo"
      );
      setModelProduct(REF_MODEL?.value);
      setIsModelValueLoaded(true);

      const REF_SIZE_CODE = specsData?.[1];
      const REF_SIZE = REF_SIZE_CODE?.specs.find(
        (item) => item.name === "Tamanho"
      );
      setSizeProduct(REF_SIZE?.value);
      setIsSizeValueLoaded(true);
    }
  }, [specsData, brand]);

  useEffect(() => {
    if (modelProduct && handleModel) {
      handleModel(modelProduct);
    }
  }, [handleModel, modelProduct]);

  useEffect(() => {
    if (sizeProduct && handleSize) {
      handleSize(sizeProduct);
    }
  }, [handleSize, sizeProduct]);

  useEffect(() => {
    if (isLoading === false && isModelValueLoaded && isSizeValueLoaded) {
      hasProductSpecCalled(isLoading);
    }
  }, [hasProductSpecCalled, isLoading, isModelValueLoaded, isSizeValueLoaded]);

  const { isClientMobile } = useAppContext();
  const { isClientTablet } = useAppContext();

  const [isProductSpecsCollapsed, setIsProductSpecsCollapsed] = useState(false);

  const toggleShowCollapsible = () => {
    setIsProductSpecsCollapsed((state) => !state);
  };

  const shouldShowBoxSpecs = isClientMobile ? isProductSpecsCollapsed : true;

  const iconsColor = isBlackTheme
    ? theme.colors.blackFriday.white
    : theme.colors.neutral["550"];
  const weigthIconColor = isBlackTheme
    ? theme.colors.blackFriday.white
    : theme.colors.blackFriday.black;
  const arrowColor = isBlackTheme
    ? theme.colors.blackFriday.white
    : theme.colors.primary["200"];
  const PDFArrowColor = isBlackTheme
    ? theme.colors.blackFriday["300"]
    : theme.colors.blackFriday["480"];

  const souldFlixMediaAppear =
    (modelProduct && brand !== "Tramontina") ||
    (bareanId13 && brand === "Tramontina");

  return (
    <Container id={idSection}>
      {isClientTablet && (
        <AggregatorCustom>
          <ButtonCustom
            onClick={toggleShowCollapsible}
            isBlackTheme={isBlackTheme}
            data-testid="product-specs-button"
          >
            <TextCustom
              isClientTablet={isClientTablet}
              isClientMobile={isClientMobile}
              isBlackTheme={isBlackTheme}
              data-testid="product-specs-button-text"
            >
              Ficha técnica
            </TextCustom>
            {isProductSpecsCollapsed ? (
              <CollapseArrowUp color={arrowColor} />
            ) : (
              <CollapseArrowDown color={arrowColor} />
            )}
          </ButtonCustom>
        </AggregatorCustom>
      )}
      {!isClientTablet && (
        <Title isBlackTheme={isBlackTheme}>Ficha técnica</Title>
      )}
      {souldFlixMediaAppear ? (
        <FlixMedia
          productModel={brand === "Tramontina" ? bareanId13 : modelProduct}
          productBrand={brand}
          distributorCode={flixMediaObject.id}
        />
      ) : null}
      <Syndigo codeInside={idProduct} />
      <StandOut />
      <ContainerInfo Collapse={isClientTablet && !isProductSpecsCollapsed}>
        <SectionInfo>
          {shouldShowBoxSpecs && (
            <BoxSpecs
              isClientMobile={isClientMobile}
              isClientTablet={isClientTablet}
              isBlackTheme={isBlackTheme}
            >
              {specsData?.length
                ? specsData?.map((item, index) => {
                    const currentIndex = index;

                    return (
                      <Group key={`${item?.id}-${currentIndex}`}>
                        <TitleBlockTable
                          isClientMobile={isClientMobile}
                          isClientTablet={isClientTablet}
                          isBlackTheme={isBlackTheme}
                        >
                          {item?.name}
                        </TitleBlockTable>
                        <Table>
                          <BodyTable>
                            {item?.specs?.map(
                              (itemSpec) =>
                                itemSpec?.value && (
                                  <LineTable
                                    key={`${itemSpec?.id}-${itemSpec?.name}`}
                                    isBlackTheme={isBlackTheme}
                                  >
                                    <CellTable
                                      isClientMobile={isClientMobile}
                                      isClientTablet={isClientTablet}
                                    >
                                      {itemSpec?.name}
                                    </CellTable>
                                    <CellTable
                                      isClientMobile={isClientMobile}
                                      isClientTablet={isClientTablet}
                                    >
                                      {`${itemSpec?.value} ${
                                        itemSpec?.unitMeasurement?.includes(
                                          "kwh"
                                        )
                                          ? itemSpec?.unitMeasurement
                                          : ""
                                      }`}
                                    </CellTable>
                                  </LineTable>
                                )
                            )}
                          </BodyTable>
                        </Table>
                      </Group>
                    );
                  })
                : null}
            </BoxSpecs>
          )}
        </SectionInfo>
        <SectionSub
          isClientMobile={isClientMobile}
          isClientTablet={isClientTablet}
        >
          <BoxDimension
            isClientMobile={isClientMobile}
            isClientTablet={isClientTablet}
            Collapse={isClientTablet && !isProductSpecsCollapsed}
            isBlackTheme={isBlackTheme}
          >
            {dimensionData?.length ? (
              <Group>
                <TitleBlockTable
                  isClientMobile={isClientMobile}
                  isClientTablet={isClientTablet}
                  isBlackTheme={isBlackTheme}
                >
                  Dimensões do produto
                </TitleBlockTable>
                <Margin1 />
                <Row>
                  <BoxAlign>
                    <DimensionArrowWidth
                      width={20}
                      height={20}
                      color={iconsColor}
                    />
                    <SubtitleDimension isBlackTheme={isBlackTheme}>
                      Largura:
                    </SubtitleDimension>
                  </BoxAlign>
                  <Value isBlackTheme={isBlackTheme}>
                    {formatNumber(dimensionData?.width || 0)} cm
                  </Value>
                </Row>
                <Row>
                  <BoxAlign>
                    <DimensionArrowHeight
                      width={18}
                      height={18}
                      color={iconsColor}
                    />
                    <SubtitleDimension isBlackTheme={isBlackTheme}>
                      Altura:
                    </SubtitleDimension>
                  </BoxAlign>
                  <Value isBlackTheme={isBlackTheme}>
                    {formatNumber(dimensionData?.height || 0)} cm
                  </Value>
                </Row>
                <Row>
                  <BoxAlign>
                    <DimensionArrowLength
                      width={18}
                      height={18}
                      color={iconsColor}
                    />
                    <SubtitleDimension isBlackTheme={isBlackTheme}>
                      Profundidade:
                    </SubtitleDimension>
                  </BoxAlign>
                  <Value isBlackTheme={isBlackTheme}>
                    {formatNumber(dimensionData?.length || 0)} cm
                  </Value>
                </Row>
                <Row>
                  <BoxAlign>
                    <DimensionWeightIcon
                      width={16}
                      height={16}
                      color={weigthIconColor}
                    />
                    <SubtitleDimension isBlackTheme={isBlackTheme}>
                      Peso:
                    </SubtitleDimension>
                  </BoxAlign>
                  <Value isBlackTheme={isBlackTheme}>
                    {formatNumber(dimensionData?.weightUnity || 0)} kg
                  </Value>
                </Row>
                <Row>
                  <BoxAlign>
                    <DimensionWeightIcon
                      width={16}
                      height={16}
                      color={weigthIconColor}
                    />
                    <SubtitleDimension isBlackTheme={isBlackTheme}>
                      Peso Embalado:
                    </SubtitleDimension>
                  </BoxAlign>
                  <Value isBlackTheme={isBlackTheme}>
                    {formatNumber(dimensionData?.grossWeight || 0)} kg
                  </Value>
                </Row>
              </Group>
            ) : null}
          </BoxDimension>
          {linksData?.length && (
            <BoxLinks
              isClientTablet={isClientTablet}
              isClientMobile={isClientMobile}
              Collapse={isClientTablet && !isProductSpecsCollapsed}
              isBlackTheme={isBlackTheme}
            >
              <Group isClientMobile={isClientMobile}>
                <TitleBlockTable
                  isClientMobile={isClientMobile}
                  isClientTablet={isClientTablet}
                  isBlackTheme={isBlackTheme}
                >
                  Links úteis
                </TitleBlockTable>
                <Margin1 />
                {linksData?.map((document) => (
                  <LinkPDF
                    key={`${document.codeCD}#${document.sequenceNumber}`}
                    target="_blank"
                    href={buildUrlUsefulLinks(document)}
                    isBlackTheme={isBlackTheme}
                  >
                    {document?.linkName}
                    <PDFArrow height={25} color={PDFArrowColor} />
                  </LinkPDF>
                ))}
              </Group>
            </BoxLinks>
          )}
        </SectionSub>
      </ContainerInfo>
    </Container>
  );
};

export default ProductSpec;
